// src/layouts/hospital-dashboard/index.js
import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DataTable from "examples/Tables/DataTable";

// API
import { hospitalDashboardApi } from "api/hospitalDashboardApi";

// Helper functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "UGX",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
};

function HospitalDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await hospitalDashboardApi.getHospitalDashboardData();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch hospital dashboard data:", err);
      setError("Failed to load dashboard data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <MDTypography variant="h5">Loading hospital dashboard...</MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h5" color="error">
                {error}
              </MDTypography>
            </MDBox>
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const stats = dashboardData?.statistics || {};
  const hospitalInfo = dashboardData?.hospitalInfo || {};

  // Prepare chart data
  const weeklyChartData = {
    labels: dashboardData?.weeklyClaims?.map((item) => item.label) || [],
    datasets: {
      label: "Claims",
      data: dashboardData?.weeklyClaims?.map((item) => item.value) || [],
    },
  };

  const monthlyChartData = {
    labels: dashboardData?.monthlyClaims?.map((item) => item.label) || [],
    datasets: {
      label: "Monthly Claims",
      data: dashboardData?.monthlyClaims?.map((item) => item.value) || [],
    },
  };

  const statusChartData = {
    labels: dashboardData?.claimsByStatus?.map((item) => item.label) || [],
    datasets: {
      label: "Claims by Status",
      data: dashboardData?.claimsByStatus?.map((item) => item.value) || [],
    },
  };

  // Prepare terminals table
  const terminalsColumns = [
    { Header: "Terminal Code", accessor: "code", width: "40%" },
    { Header: "Claims Count", accessor: "claims", width: "30%" },
    { Header: "Total Amount", accessor: "amount", width: "30%" },
  ];

  const terminalsRows =
    dashboardData?.claimsByTerminal?.map((terminal, index) => ({
      code: terminal.label,
      claims: terminal.value,
      amount: formatCurrency(terminal.amount),
      id: index,
    })) || [];

  // Prepare recent claims table
  const recentClaimsColumns = [
    { Header: "Reference ID", accessor: "referenceId", width: "25%" },
    { Header: "Card Number", accessor: "cardNumber", width: "20%" },
    { Header: "Amount", accessor: "amount", width: "15%" },
    { Header: "Status", accessor: "status", width: "15%" },
    { Header: "Date", accessor: "date", width: "25%" },
  ];

  const recentClaimsRows =
    dashboardData?.recentClaims?.map((claim) => ({
      referenceId: claim.referenceId,
      cardNumber: claim.cardNumber,
      amount: formatCurrency(claim.totalAmount),
      status: (
        <MDTypography
          variant="caption"
          fontWeight="medium"
          color={claim.status === "Success" ? "success" : "error"}
        >
          {claim.status}
        </MDTypography>
      ),
      date: new Date(claim.createdAt).toLocaleDateString(),
      id: claim.id,
    })) || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Hospital Info Header */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={3}>
            <MDTypography variant="h5" gutterBottom>
              Welcome, {hospitalInfo.name}
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Hospital Code: {hospitalInfo.code} | Admin: {hospitalInfo.adminName} | Active
              Terminals: {stats.activeTerminals}
            </MDTypography>
          </MDBox>
        </Card>

        {/* Statistics Cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="receipt"
                title="Total Claims"
                count={formatNumber(stats.totalClaims)}
                percentage={{
                  color: "success",
                  amount: `+${
                    stats.weeklyClaims > 0
                      ? Math.round((stats.weeklyClaims / stats.totalClaims) * 100)
                      : 0
                  }%`,
                  label: "than last week",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="today"
                title="Today's Claims"
                count={stats.todayClaims}
                percentage={{
                  color: "info",
                  amount: "",
                  label: "so far today",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="attach_money"
                title="Total Revenue"
                count={formatCurrency(stats.totalAmount)}
                percentage={{
                  color: "success",
                  amount: `+${Math.round((stats.weeklyAmount / stats.totalAmount) * 100)}%`,
                  label: "than last week",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="trending_up"
                title="Success Rate"
                count={`${Math.round(stats.successRate)}%`}
                percentage={{
                  color: "success",
                  amount: "",
                  label: `${stats.successClaims} successful claims`,
                }}
              />
            </MDBox>
          </Grid>
        </Grid>

        {/* Charts */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="info"
                  title="Weekly Claims"
                  description="Claims submitted this week"
                  date="Last 7 days"
                  chart={weeklyChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="success"
                  title="Monthly Claims Trend"
                  description={
                    <>
                      (
                      <strong>
                        +
                        {stats.monthlyClaims > 0
                          ? Math.round((stats.monthlyClaims / (stats.totalClaims || 1)) * 100)
                          : 0}
                        %
                      </strong>
                      ) increase this month
                    </>
                  }
                  date="This year"
                  chart={monthlyChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="dark"
                  title="Claims by Status"
                  description="Distribution of claim statuses"
                  date="All time"
                  chart={statusChartData}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Additional Stats Row */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <ComplexStatisticsCard
                color="info"
                icon="computer"
                title="Active Terminals"
                count={stats.activeTerminals}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "active devices",
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ComplexStatisticsCard
                color="warning"
                icon="swap_horiz"
                title="Reversed Claims"
                count={stats.reversedClaims}
                percentage={{
                  color: "warning",
                  amount: "",
                  label: "claims reversed",
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ComplexStatisticsCard
                color="secondary"
                icon="calculate"
                title="Average Claim"
                count={formatCurrency(stats.averageClaimAmount)}
                percentage={{
                  color: "info",
                  amount: "",
                  label: "per claim",
                }}
              />
            </Grid>
          </Grid>
        </MDBox>

        {/* Tables */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={6}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" gutterBottom>
                    Terminal Performance
                  </MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Claims by terminal device
                  </MDTypography>
                </MDBox>
                <DataTable
                  table={{ columns: terminalsColumns, rows: terminalsRows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" gutterBottom>
                    Recent Claims
                  </MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Latest 10 claims submitted
                  </MDTypography>
                </MDBox>
                <DataTable
                  table={{ columns: recentClaimsColumns, rows: recentClaimsRows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default HospitalDashboard;
